import React, { useCallback, useState } from 'react';
import {
  MdAccountTree,
  MdExpandLess,
  MdExpandMore,
  MdLock,
  MdPlayArrow,
} from 'react-icons/md';
import {
  Avatar,
  Button,
  IconButton,
  StatusPill,
  STATUS_PILL_TONE_NEUTRAL,
  STATUS_PILL_TONE_SUCCESS,
} from '@aibuild/ui';
import type { ProcessListItemDto } from '@api/processes';
import {
  formatDuration,
  formatRelativeTime,
} from '../utils/formatRelativeTime';
import ProcessRowActions from './ProcessRowActions';
import {
  DetailsDescription,
  DetailsPanel,
  DetailsStat,
  DetailsStatLabel,
  DetailsStatOwner,
  DetailsStatValue,
  DetailsStatsGrid,
  IconTile,
  ListItem,
  ListRoot,
  MetaLine,
  MetaOwner,
  MetaSep,
  MetaUpdated,
  NameRenameInput,
  NameStack,
  NameTitle,
  RowActionsEnd,
  RowMain,
} from './ProcessesList.styled';

export interface ProcessesListProps {
  processes: readonly ProcessListItemDto[];
  onOpen: (process: ProcessListItemDto) => void;
  /**
   * Fired by the per-row "Run" button on Published processes. Distinct from
   * `onOpen` so consumers can route Run to a launch dialog rather than the
   * editor — Open + Row click both go to `onOpen`.
   */
  onRun: (process: ProcessListItemDto) => void;
  onRename: (process: ProcessListItemDto, newName: string) => void;
  onDuplicate: (process: ProcessListItemDto) => void;
  onSendCopy: (process: ProcessListItemDto) => void;
  onDelete: (process: ProcessListItemDto) => void;
  isMutating?: boolean;
  dataTestId?: string;
}

interface ProcessRowProps {
  process: ProcessListItemDto;
  expanded: boolean;
  isRenaming: boolean;
  onStartRename: () => void;
  onCommitRename: (newName: string) => void;
  onCancelRename: () => void;
  onToggleExpand: () => void;
  onOpen: ProcessesListProps['onOpen'];
  onRun: ProcessesListProps['onRun'];
  onDuplicate: ProcessesListProps['onDuplicate'];
  onSendCopy: ProcessesListProps['onSendCopy'];
  onDelete: ProcessesListProps['onDelete'];
  isMutating: boolean;
  dataTestId: string;
}

interface RenameEditorProps {
  initialName: string;
  ariaLabel: string;
  onCommit: (trimmed: string) => void;
  onCancel: () => void;
  dataTestId: string;
}

/**
 * Tiny dedicated editor for the inline rename. Owns its own draft state so
 * the rename input can initialise from `initialName` on mount (no
 * `setState`-inside-`useEffect` dance), and so unmounting on cancel/commit
 * cleans up the draft automatically.
 */
const RenameEditor: React.FC<RenameEditorProps> = ({
  initialName,
  ariaLabel,
  onCommit,
  onCancel,
  dataTestId,
}) => {
  const [draft, setDraft] = useState(initialName);

  const commitIfChanged = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === initialName) {
      onCancel();
      return;
    }
    onCommit(trimmed);
  };

  return (
    <NameRenameInput
      ref={(el) => {
        if (!el) return;
        // Autofocus + select-all on mount. Using a ref callback (rather than
        // `useEffect`) keeps the focus call inline with the DOM lifecycle
        // and avoids any "useEffect setState" lint complaints elsewhere.
        if (document.activeElement !== el) {
          el.focus();
          el.select();
        }
      }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          commitIfChanged();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onBlur={commitIfChanged}
      maxLength={100}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    />
  );
};

/** Keys that activate the row when it has keyboard focus (treats it as a button). */
const isRowActivationKey = (key: string): boolean =>
  key === 'Enter' || key === ' ';

interface ProcessRowEndProps {
  process: ProcessListItemDto;
  expanded: boolean;
  isMutating: boolean;
  onOpen: ProcessesListProps['onOpen'];
  onRun: ProcessesListProps['onRun'];
  onToggleExpand: () => void;
  onStartRename: () => void;
  onDuplicate: ProcessesListProps['onDuplicate'];
  onSendCopy: ProcessesListProps['onSendCopy'];
  onDelete: ProcessesListProps['onDelete'];
  dataTestId: string;
}

/**
 * Trailing cluster: status pill · Run/Open button · expand toggle · kebab.
 * Extracted from `ProcessRow` so the parent's cognitive complexity stays
 * inside Sonar's 15-branch budget — every status/expanded ternary lives
 * here instead of bloating the parent.
 */
const ProcessRowEnd: React.FC<ProcessRowEndProps> = ({
  process: p,
  expanded,
  isMutating,
  onOpen,
  onRun,
  onToggleExpand,
  onStartRename,
  onDuplicate,
  onSendCopy,
  onDelete,
  dataTestId,
}) => {
  const isPublished = p.status === 'published';
  return (
    <RowActionsEnd onClick={(e) => e.stopPropagation()}>
      <StatusPill
        tone={isPublished ? STATUS_PILL_TONE_SUCCESS : STATUS_PILL_TONE_NEUTRAL}
        icon={isPublished ? <MdLock /> : undefined}
        dot={!isPublished}
        dataTestId={`${dataTestId}__status-${p.processId}`}
      >
        {isPublished ? 'Published' : 'Draft'}
      </StatusPill>
      {isPublished ? (
        <Button
          variant="tonal"
          size="sm"
          onClick={() => onRun(p)}
          leadingIcon={<MdPlayArrow size={16} />}
          dataTestId={`${dataTestId}__run-${p.processId}`}
        >
          Run
        </Button>
      ) : (
        <Button
          variant="outlined"
          size="sm"
          onClick={() => onOpen(p)}
          dataTestId={`${dataTestId}__open-${p.processId}`}
        >
          Open
        </Button>
      )}
      <IconButton
        ariaLabel={expanded ? 'Hide details' : 'Show details'}
        icon={expanded ? <MdExpandLess /> : <MdExpandMore />}
        onClick={onToggleExpand}
        aria-expanded={expanded}
        aria-controls={`${dataTestId}__details-${p.processId}`}
        dataTestId={`${dataTestId}__expand-${p.processId}`}
      />
      <ProcessRowActions
        process={p}
        onRename={onStartRename}
        onDuplicate={onDuplicate}
        onSendCopy={onSendCopy}
        onDelete={onDelete}
        disabled={isMutating}
        dataTestId={`${dataTestId}__actions-${p.processId}`}
      />
    </RowActionsEnd>
  );
};

interface ProcessRowDetailsProps {
  process: ProcessListItemDto;
  dataTestId: string;
}

/**
 * Expanded details panel: description (when present) + a 6-up stat grid.
 * Extracted from `ProcessRow` to keep the parent's cognitive complexity
 * inside Sonar's 15-branch budget — the `description &&` short-circuit
 * lives here rather than in the parent.
 */
const ProcessRowDetails: React.FC<ProcessRowDetailsProps> = ({
  process: p,
  dataTestId,
}) => (
  <DetailsPanel
    id={`${dataTestId}__details-${p.processId}`}
    data-testid={`${dataTestId}__details-${p.processId}`}
  >
    {p.description && (
      <DetailsDescription>{p.description}</DetailsDescription>
    )}
    <DetailsStatsGrid>
      <DetailsStat>
        <DetailsStatLabel>Runs</DetailsStatLabel>
        <DetailsStatValue>{p.runCount.toLocaleString()}</DetailsStatValue>
      </DetailsStat>
      <DetailsStat>
        <DetailsStatLabel>Avg time</DetailsStatLabel>
        <DetailsStatValue>{formatDuration(p.avgDurationMs)}</DetailsStatValue>
      </DetailsStat>
      <DetailsStat>
        <DetailsStatLabel>Updated</DetailsStatLabel>
        <DetailsStatValue>{formatRelativeTime(p.updatedAt)}</DetailsStatValue>
      </DetailsStat>
      <DetailsStat>
        <DetailsStatLabel>Last run</DetailsStatLabel>
        <DetailsStatValue>{formatRelativeTime(p.lastRunAt)}</DetailsStatValue>
      </DetailsStat>
      <DetailsStat>
        <DetailsStatLabel>Nodes</DetailsStatLabel>
        <DetailsStatValue>{p.nodeCount}</DetailsStatValue>
      </DetailsStat>
      <DetailsStat>
        <DetailsStatLabel>Owner</DetailsStatLabel>
        <DetailsStatOwner>
          <Avatar name={p.createdBy.name} size={20} />
          {p.createdBy.name}
        </DetailsStatOwner>
      </DetailsStat>
    </DetailsStatsGrid>
  </DetailsPanel>
);

/**
 * Single row + optional expanded details panel. Kept as its own component so
 * each row can own its `useCallback` handlers without re-rendering the
 * sibling rows.
 */
const ProcessRow: React.FC<ProcessRowProps> = ({
  process: p,
  expanded,
  isRenaming,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onToggleExpand,
  onOpen,
  onRun,
  onDuplicate,
  onSendCopy,
  onDelete,
  isMutating,
  dataTestId,
}) => {
  const handleRowKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isRenaming || !isRowActivationKey(e.key)) return;
      e.preventDefault();
      onOpen(p);
    },
    [isRenaming, onOpen, p],
  );

  const handleRowClick = () => {
    if (isRenaming) return;
    onOpen(p);
  };

  return (
    <ListItem>
      <RowMain
        role="button"
        tabIndex={isRenaming ? -1 : 0}
        aria-label={`Open process: ${p.name}`}
        onClick={handleRowClick}
        onKeyDown={handleRowKey}
        data-expanded={expanded || undefined}
        data-testid={`${dataTestId}__row-${p.processId}`}
      >
        <IconTile aria-hidden="true">
          <MdAccountTree />
        </IconTile>
        <NameStack>
          {isRenaming ? (
            <RenameEditor
              initialName={p.name}
              ariaLabel={`Rename ${p.name}`}
              onCommit={onCommitRename}
              onCancel={onCancelRename}
              dataTestId={`${dataTestId}__rename-input-${p.processId}`}
            />
          ) : (
            <NameTitle
              title={p.name}
              onDoubleClick={
                p.isOwner
                  ? (e) => {
                      e.stopPropagation();
                      onStartRename();
                    }
                  : undefined
              }
              data-testid={`${dataTestId}__name-${p.processId}`}
            >
              {p.name}
            </NameTitle>
          )}
          <MetaLine>
            <MetaOwner
              title={`Owned by ${p.createdBy.name}`}
              data-testid={`${dataTestId}__owner-${p.processId}`}
            >
              <Avatar name={p.createdBy.name} size={18} />
              {p.createdBy.name}
            </MetaOwner>
            <MetaSep aria-hidden="true">·</MetaSep>
            <MetaUpdated>
              Updated {formatRelativeTime(p.updatedAt)}
            </MetaUpdated>
          </MetaLine>
        </NameStack>
        <ProcessRowEnd
          process={p}
          expanded={expanded}
          isMutating={isMutating}
          onOpen={onOpen}
          onRun={onRun}
          onToggleExpand={onToggleExpand}
          onStartRename={onStartRename}
          onDuplicate={onDuplicate}
          onSendCopy={onSendCopy}
          onDelete={onDelete}
          dataTestId={dataTestId}
        />
      </RowMain>
      {expanded && <ProcessRowDetails process={p} dataTestId={dataTestId} />}
    </ListItem>
  );
};

/**
 * Vertical list of processes (one card per row). Semantically a `<ul>` of
 * `<li>` items — each row is a button-like card with the process name + meta
 * line on the left and a status pill / Run-or-Open / expand-toggle / kebab
 * cluster on the right. Clicking the expand toggle reveals an in-line
 * details panel with description + stats.
 */
const ProcessesList: React.FC<ProcessesListProps> = ({
  processes,
  onOpen,
  onRun,
  onRename,
  onDuplicate,
  onSendCopy,
  onDelete,
  isMutating = false,
  dataTestId = 'processes-list',
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const toggleExpand = useCallback((processId: string) => {
    setExpandedId((current) => (current === processId ? null : processId));
  }, []);

  return (
    <ListRoot aria-label="Processes" data-testid={dataTestId}>
      {processes.map((p) => (
        <ProcessRow
          key={p.processId}
          process={p}
          expanded={expandedId === p.processId}
          isRenaming={renamingId === p.processId}
          onStartRename={() => setRenamingId(p.processId)}
          onCancelRename={() => setRenamingId(null)}
          onCommitRename={(newName) => {
            setRenamingId(null);
            onRename(p, newName);
          }}
          onToggleExpand={() => toggleExpand(p.processId)}
          onOpen={onOpen}
          onRun={onRun}
          onDuplicate={onDuplicate}
          onSendCopy={onSendCopy}
          onDelete={onDelete}
          isMutating={isMutating}
          dataTestId={dataTestId}
        />
      ))}
    </ListRoot>
  );
};

export default ProcessesList;
